/**
 * Custom hook for synchronizing deckline state with real-time KPI Library subscription events.
 *
 * Registers listeners with KPISubscriptionContext for KPI variant update/delete events
 * and reconciles the local deckline state (both original and modified) with incoming
 * changes from the backend.
 *
 * Handles:
 * - Replacing updated decklines from subscription events
 * - Removing deleted decklines
 * - Applying reordered decklines (serial_no changes after deletion)
 * - Edge case: locally-assigned variant that backend doesn't know about
 * - Toast notifications when decklines are reset
 */

import { NotificationProps } from '@components/Common/Notification';
import { useKPISubscriptionContext } from '@context/KPISubscriptionContext';
import { EnhancedComment } from '@deckManagement/components/Comments/CommentsDrawer.types';
import {
  DatasetSource,
  Deckline,
  OnDeleteKPIVariantSubscription,
  OnUpdateKPIVariantSubscription,
} from '@services/api/deckManagement/graphql/schema/API';
import { useGetRootKPIs } from '@services/api/deckManagement/hooks/useRootKPIs';
import { parseIdFromKey } from '@utils/deckHelpers';
import { applyVariantOverridesToDeckline, determineDecklineType, extractActualKpiName } from '@utils/decklineHelpers';
import { changeMultipleEntryFields, initializeMetadata } from '@utils/deckManagementEntityHelpers';
import { asGalaxyDrivers, datasetSourceFromDrivers, EMPTY_KPI_DRIVERS, readPageDrivers } from '@utils/kpiDriverHelpers';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { AnyKPIDrivers } from 'types/PageSidePanel.types';

interface UseKPIDecklineSyncParams {
  enabled: boolean;
  pageId: string;
  setModifiedPageDecklines: Dispatch<SetStateAction<EnhancedDeckline[]>>;
  pageDriverSets: Partial<Record<DatasetSource, AnyKPIDrivers>> | null;
  toastNotification: (notification: NotificationProps) => void;
}

/**
 * Converts a raw Deckline from a subscription event into an EnhancedDeckline with initialized metadata.
 * Preserves the existing internalId and serial_no when replacing an existing deckline to maintain
 * selection state and local ordering (final resequencing happens at save time).
 */
const enhanceDecklineFromEvent = (
  deckline: Deckline,
  pageId: string,
  sortPos: number,
  existing?: { internalId: string; serialNo?: string | null },
): EnhancedDeckline => {
  const decklineId = parseIdFromKey(deckline.sk);
  const enhancedComments = deckline.comments?.flatMap((comment): EnhancedComment[] =>
    comment ? [{ ...comment, metadata: { isPendingSave: false, isPendingDelete: false } }] : [],
  );
  return initializeMetadata(
    {
      ...deckline,
      deckline_id: decklineId,
      deckline_type: determineDecklineType(deckline),
      actual_kpi_name: extractActualKpiName(deckline),
      page_id: pageId,
      comments: enhancedComments,
      // Preserve local serial_no to maintain ordering with locally-inserted rows
      ...(existing?.serialNo && { serial_no: existing.serialNo }),
    },
    { sortPos, ...(existing?.internalId && { internalId: existing.internalId }) },
  );
};

/**
 * Checks if a deckline has meaningful modifications beyond serial_no shifts.
 * Serial number changes are a side-effect of inserting/removing rows and are not
 * user-visible, so they should not trigger "unsaved changes lost" notifications.
 */
const hasMeaningfulModifications = (deckline: EnhancedDeckline): boolean => {
  if (!deckline.metadata.isModified) return false;
  const { fieldChanges } = deckline.metadata;
  if (!fieldChanges) return true;
  const changedKeys = Object.keys(fieldChanges);
  // If no field changes tracked, trust isModified flag (modification happened outside fieldChanges tracking)
  if (changedKeys.length === 0) return true;
  // If only serial_no changed, it's a positional side-effect, not a meaningful user edit
  return changedKeys.some((key) => key !== 'serial_no');
};

/**
 * Hook that subscribes to KPI page events and synchronizes deckline state accordingly.
 */
const useKPIDecklineSync = ({
  enabled,
  pageId,
  setModifiedPageDecklines,
  pageDriverSets,
  toastNotification,
}: UseKPIDecklineSyncParams): void => {
  const { data: rootKPIs = [] } = useGetRootKPIs();

  // Deduplication: AppSync may deliver the same event multiple times
  const lastProcessedEventRef = useRef<string>('');

  /** Resolves a root_kpi_id to its display name */
  const resolveRootKpiName = useCallback(
    (rootKpiId: string): string => {
      return rootKPIs.find((kpi) => kpi.root_kpi_id === rootKpiId)?.root_kpi_name ?? rootKpiId;
    },
    [rootKPIs],
  );

  /** Handles onUpdateKPIVariant subscription events */
  const handleUpdateEvent = useCallback(
    (data: OnUpdateKPIVariantSubscription) => {
      const event = data.onUpdateKPIVariant;
      if (!event || !pageDriverSets || !pageId) return;

      // Deduplicate: AppSync may deliver the same event multiple times
      const eventKey = `update:${event.kpi_variant_id}:${event.last_updated_date}`;
      if (lastProcessedEventRef.current === eventKey) return;
      lastProcessedEventRef.current = eventKey;

      const updatedDecklines = event.updated_decklines;
      const variantOverrides = event.variant_overrides;
      const variantId = event.kpi_variant_id;
      const variantName = event.variant_name;

      // Filter updated decklines to those belonging to the current page. The subscription payload's
      // deckline shape is runtime-identical to Deckline but codegen types its driver sub-objects
      // inline (post KPIDrivers-interface), so it no longer matches Deckline nominally — cast.
      const relevantUpdates = (updatedDecklines ?? []).filter(
        (d) => !!d && d.pk.includes(pageId),
      ) as unknown as Deckline[];
      const updatedDecklineIds = new Set(relevantUpdates.map((d) => parseIdFromKey(d.sk)));

      // Replace updated decklines in modifiedPageDecklines only.
      // We skip originalPageDecklines to avoid triggering the node resolution effect (which sets isLoading=true
      // and causes UI flicker). This is safe because replaced decklines have isModified=false, so they won't
      // appear in save changesets.
      if (relevantUpdates.length > 0 || (variantOverrides && variantId)) {
        let affectedNames: string[] = [];
        setModifiedPageDecklines((prev) => {
          const names: string[] = [];
          const next = prev.map((existing, idx) => {
            // Check if this deckline is in the server-provided updated list
            if (existing.deckline_id && updatedDecklineIds.has(existing.deckline_id)) {
              const incoming = relevantUpdates.find((d) => parseIdFromKey(d.sk) === existing.deckline_id);
              if (incoming) {
                if (hasMeaningfulModifications(existing)) {
                  names.push(incoming.display_name || incoming.kpi_name || variantName);
                }
                return enhanceDecklineFromEvent(incoming, pageId, idx, {
                  internalId: existing.metadata.internalId,
                  serialNo: existing.serial_no,
                });
              }
            }

            // Edge case: locally-assigned variant not yet saved to API.
            // The backend doesn't know about this assignment, so it won't appear in updated_decklines.
            if (
              variantOverrides &&
              variantId &&
              existing.kpi_variant_id === variantId &&
              existing.deckline_id &&
              !updatedDecklineIds.has(existing.deckline_id)
            ) {
              // Only toast for existing rows with meaningful modifications — new rows haven't been saved yet
              if (!existing.metadata.isNewRow && hasMeaningfulModifications(existing)) {
                names.push(existing.display_name || existing.kpi_name || variantName);
              }
              // Merge base is the page-driver entry for this deckline's own dataset. The
              // variant-override merge itself is still Galaxy-typed; making it dataset-aware is the
              // HCTR-variant-display feature's job.
              return applyVariantOverridesToDeckline(
                existing,
                asGalaxyDrivers(variantOverrides) ?? {},
                asGalaxyDrivers(readPageDrivers(pageDriverSets, datasetSourceFromDrivers(existing.deckline_drivers))) ??
                  EMPTY_KPI_DRIVERS,
              );
            }

            return existing;
          });
          affectedNames = names;
          return next;
        });

        // Toasts fired outside updater to avoid Strict Mode double-invocation
        affectedNames.forEach((name) => {
          toastNotification({
            message: `Deckline "${name}" was reset due to KPI Library changes. Unsaved changes were lost.`,
            visible: true,
            type: 'warning',
            duration: 5000,
          });
        });
      }
    },
    [pageDriverSets, pageId, setModifiedPageDecklines, toastNotification],
  );

  /** Handles onDeleteKPIVariant subscription events */
  const handleDeleteEvent = useCallback(
    (data: OnDeleteKPIVariantSubscription) => {
      const event = data.onDeleteKPIVariant;
      if (!event || !pageId) return;

      // Deduplicate: AppSync may deliver the same event multiple times
      const eventKey = `delete:${event.kpi_variant_id}`;
      if (lastProcessedEventRef.current === eventKey) return;
      lastProcessedEventRef.current = eventKey;

      const deletedDecklines = event.deleted_decklines;
      const reorderedDecklines = event.reordered_decklines;
      const rootKpiId = event.root_kpi_id;

      // Filter to decklines belonging to the current page. Cast for the same reason as the update
      // handler: the event's deckline shape is runtime-identical but no longer nominally `Deckline`.
      const relevantDeletes = (deletedDecklines ?? []).filter(
        (d) => !!d && d.pk.includes(pageId),
      ) as unknown as Deckline[];
      const relevantReorders = (reorderedDecklines ?? []).filter(
        (d) => !!d && d.pk.includes(pageId),
      ) as unknown as Deckline[];

      const deletedIds = new Set(relevantDeletes.map((d) => parseIdFromKey(d.sk)));
      const reorderMap = new Map(relevantReorders.map((d) => [parseIdFromKey(d.sk), d]));
      const deletedVariantId = event.kpi_variant_id;

      if (deletedIds.size === 0 && reorderMap.size === 0 && !deletedVariantId) return;

      let unlinkedNames: string[] = [];
      setModifiedPageDecklines((prev) => {
        const names: string[] = [];
        const next = prev
          .filter((deckline) => !deckline.deckline_id || !deletedIds.has(deckline.deckline_id))
          .map((deckline, idx) => {
            if (deckline.deckline_id && reorderMap.has(deckline.deckline_id)) {
              return enhanceDecklineFromEvent(reorderMap.get(deckline.deckline_id)!, pageId, idx, {
                internalId: deckline.metadata.internalId,
              });
            }
            // Unlink new decklines that reference the deleted variant
            if (deckline.metadata.isNewRow && deckline.kpi_variant_id === deletedVariantId) {
              names.push(deckline.display_name || deckline.kpi_name || '');
              return changeMultipleEntryFields(deckline, {
                kpi_variant_id: null,
                root_kpi_id: null,
                deckline_drivers: null,
                kpi_id: '',
                is_exception: null,
              });
            }
            return deckline;
          });
        unlinkedNames = names;
        return next;
      });

      // Toasts fired outside updater to avoid Strict Mode double-invocation
      unlinkedNames.forEach((name) => {
        toastNotification({
          message: `Deckline "${name}" was unlinked — its KPI variant was deleted.`,
          visible: true,
          type: 'warning',
          duration: 5000,
        });
      });

      if (deletedIds.size > 0) {
        const rootKpiName = resolveRootKpiName(rootKpiId);
        toastNotification({
          message: `${deletedIds.size} Deckline(s) removed due to KPI Library changes (${rootKpiName})`,
          visible: true,
          type: 'warning',
        });
      }
    },
    [pageId, resolveRootKpiName, setModifiedPageDecklines, toastNotification],
  );

  // Register listeners and page-scoped filtering via KPI Subscription context
  const { registerListener } = useKPISubscriptionContext();

  // Register event listeners with page scope for server-side deckline filtering
  useEffect(() => {
    if (!enabled) return undefined;
    return registerListener(
      {
        onUpdateKPIVariant: handleUpdateEvent,
        onDeleteKPIVariant: handleDeleteEvent,
      },
      { pageIds: pageId ? [pageId] : undefined },
    );
  }, [enabled, pageId, handleUpdateEvent, handleDeleteEvent, registerListener]);
};

export default useKPIDecklineSync;
